<template>
  <div class="ak-cred-field">
    <label v-if="label" class="ak-cred-field__label">{{ label }}</label>
    <div class="ak-cred-field__row">
      <v-text-field
        :model-value="displayValue"
        variant="outlined"
        density="compact"
        hide-details
        readonly
        rounded="lg"
        class="ak-cred-field__input"
      >
        <template v-if="canToggleReveal" #append-inner>
          <v-btn
            icon
            size="x-small"
            variant="text"
            color="primary"
            @click="revealed = !revealed"
          >
            <v-icon size="18">
              {{ revealed ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
            </v-icon>
            <v-tooltip activator="parent" location="top">
              {{ revealed ? 'Hide' : 'Reveal' }}
            </v-tooltip>
          </v-btn>
        </template>
      </v-text-field>

      <v-btn
        icon
        variant="text"
        color="primary"
        class="ak-cred-field__copy"
        rounded="lg"
        :disabled="!value"
        @click="copyValue"
      >
        <v-icon size="18">
          {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
        </v-icon>
        <v-tooltip activator="parent" location="top">
          {{ copied ? 'Copied' : 'Copy' }}
        </v-tooltip>
      </v-btn>
    </div>
    <p v-if="hint" class="ak-cred-field__hint">{{ hint }}</p>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { maskApiKey } from '@/utils/apiKeys'

defineOptions({ name: 'CredentialSecretField' })

const props = defineProps({
  label: { type: String, default: '' },
  value: { type: String, default: '' },
  hint: { type: String, default: '' },
  secret: { type: Boolean, default: true },
  canReveal: { type: Boolean, default: true },
})

const emit = defineEmits(['copied', 'copy-error'])

const copied = ref(false)
const revealed = ref(false)
let copyResetTimer = null

const canToggleReveal = computed(
  () => props.secret && props.canReveal && Boolean(props.value),
)

const displayValue = computed(() => {
  const raw = String(props.value ?? '').trim()
  if (!raw) return '—'
  if (!props.secret || revealed.value) return raw
  return maskApiKey(raw)
})

watch(
  () => props.value,
  () => {
    revealed.value = false
  },
)

async function copyValue() {
  const raw = String(props.value ?? '').trim()
  if (!raw) return

  try {
    await navigator.clipboard.writeText(raw)
    copied.value = true
    emit('copied')
    if (copyResetTimer) window.clearTimeout(copyResetTimer)
    copyResetTimer = window.setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    emit('copy-error')
  }
}
</script>
